import { isEmpty } from 'lodash';
import type { AxiosResponse } from 'axios';
import { useFlag } from '@loadsmart/vue-feature-flags';
import type { IWarehouse } from '@nova/core';

import apiClient from './apiClient';
import useRecaptcha, { RecaptchaTokenError } from '@/composables/useRecaptcha';

export type AppointmentBolResponse = {
  id: string;
  originalDoc: {
    id: string;
    externalKey: string;
  };
  signedDoc: {
    id: string;
    externalKey: string;
  } | null;
};

export type ValidateCheckoutForm = {
  licensePlate?: string;
  phoneNumber?: string;
};

export type ValidateCheckoutFormResponse = {
  appointmentId: string;
  assetVisitId: string;
};

export type SignBolData = {
  appointmentId: string;
  driverFullName: string;
};

export type SendBolEmailSms = {
  warehouseId: string;
  appointmentId: string;
  contactData: string;
  type: 'email' | 'sms';
};

/** Single entry for custom forms (matches check-in / backend CreateCustomFormDataDto shape) */
/*
 * Mirrors CompleteCheckoutCustomFormDataDto on the server. Both checkout paths derive
 * objectId, entityName and warehouseId themselves, and the BFF controller validates
 * with forbidNonWhitelisted -- so anything beyond these three keys is a 400.
 */
export type CompleteCheckoutCustomFormEntry = {
  formFieldId: string;
  triggerId: string;
  value?: unknown;
};

export type CompleteCheckoutData = {
  assetVisitId: string;
  appointmentId?: string;
  customFormsData?: CompleteCheckoutCustomFormEntry[];
};

function unwrap<T>(res: AxiosResponse): T {
  return (res.data?.data ?? res.data) as T;
}

export default function checkoutService() {
  const bffFlag = useFlag('enable-juno-bff');
  const { getToken } = useRecaptcha();

  const isBffEnabled = (): boolean => Boolean(bffFlag.value?.enabled);

  /**
   * Fail open: a token we could not generate must never block the driver. The request still goes
   * out, carrying an empty token plus the reason, which the backend logs as `empty-token` instead
   * of the indistinguishable `no-token`.
   */
  async function recaptchaHeaders(action: string): Promise<Record<string, string>> {
    try {
      return { 'X-Recaptcha-Token': await getToken(action) };
    } catch (err) {
      const reason = err instanceof RecaptchaTokenError ? err.reason : 'execute-failed';
      return { 'X-Recaptcha-Token': '', 'X-Recaptcha-Token-Error': reason };
    }
  }

  return {
    async getWarehouseInformation(warehouseId: string): Promise<Partial<IWarehouse>> {
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('juno_details');
        const res = await apiClient.get(`/bff/juno/${warehouseId}`, { headers });
        return unwrap(res);
      }
      const res = await apiClient.get(`/checkout/public/${warehouseId}`);
      return res.data;
    },

    async validateCheckoutForm(
      warehouseId: string,
      formData: ValidateCheckoutForm
    ): Promise<ValidateCheckoutFormResponse> {
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('juno_validate');
        const res = await apiClient.post(`/bff/juno/${warehouseId}/validate`, formData, {
          headers
        });
        return unwrap(res);
      }
      const res = await apiClient.post(`/checkout/public/${warehouseId}/validate`, formData);
      return res.data;
    },

    async getBol(warehouseId: string, appointmentId: string): Promise<AppointmentBolResponse> {
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('juno_bol');
        const res = await apiClient.post(
          `/bff/juno/${warehouseId}/bol`,
          { appointmentId },
          { headers }
        );
        return unwrap(res);
      }
      const res = await apiClient.post(`/checkout/public/${warehouseId}/bol`, { appointmentId });
      return res.data;
    },

    async previewBolPdf(warehouseId: string, documentId: string) {
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('juno_preview_bol');
        const res = await apiClient.post(
          `/bff/juno/${warehouseId}/preview-bol`,
          { documentId },
          { headers, responseType: 'blob' }
        );
        return res.data;
      }
      const res = await apiClient.post(
        `/checkout/public/${warehouseId}/preview-bol`,
        { documentId },
        { responseType: 'blob' }
      );
      return res.data;
    },

    async signBol(warehouseId: string, bolData: SignBolData) {
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('juno_sign_bol');
        const res = await apiClient.post(`/bff/juno/${warehouseId}/sign-bol`, bolData, { headers });
        return unwrap(res);
      }
      const res = await apiClient.post(`/checkout/public/${warehouseId}/sign-bol`, bolData);
      return res.data;
    },

    async sendBolEmailSms({
      warehouseId,
      appointmentId,
      contactData,
      type
    }: SendBolEmailSms): Promise<void> {
      const body = { appointmentId, value: contactData, type };
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('juno_send_bol');
        const res = await apiClient.post(`/bff/juno/${warehouseId}/send-bol`, body, { headers });
        return res.data;
      }
      const res = await apiClient.post(`/checkout/public/${warehouseId}/send-bol`, body);
      return res.data;
    },

    async completeCheckout(warehouseId: string, checkoutData: CompleteCheckoutData) {
      const body = {
        assetVisitId: checkoutData.assetVisitId,
        ...(checkoutData.appointmentId && { appointmentId: checkoutData.appointmentId }),
        ...(!isEmpty(checkoutData.customFormsData)
          ? { customFormsData: checkoutData.customFormsData }
          : {})
      };
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('juno_complete');
        const res = await apiClient.post(`/bff/juno/${warehouseId}`, body, { headers });
        return res.data;
      }
      const res = await apiClient.post(`/checkout/public/${warehouseId}`, body);
      return res.data;
    }
  };
}
